import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Brain, ArrowRight, Check, AlertCircle,
  Map, Bot, BarChart3, Target,
  Lightbulb, Zap, Gauge
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function AIServicesPage() {
  const problems = [
    'AI pilots that never reach production.',
    'Unclear use cases for AI beyond experimentation.',
    'Disconnected AI tools that don\'t integrate with your stack.'
  ];

  const outcomes = [
    { icon: Map, title: 'Clear AI roadmap', description: 'AI strategy aligned to business value.' },
    { icon: Bot, title: 'AI copilots deployed', description: 'Embedded into existing workflows.' },
    { icon: BarChart3, title: 'AI on top of BI', description: 'AI and data working together, not in a silo.' },
    { icon: Target, title: 'Measured impact', description: 'Clear governance and ROI tracking.' }
  ];

  const capabilities = [
    'AI copilots for internal users (knowledge search, summarization, Q&A)',
    'AI for analytics and BI (natural language to insights, anomaly detection)',
    'AI for recruitment and HR (screening support, candidate insights)',
    'LLM-powered chatbots and workflow automations'
  ];

  const engagements = [
    { icon: Lightbulb, title: 'AI discovery workshop', duration: '2–4 weeks', description: 'Prioritize use cases and build roadmap.' },
    { icon: Zap, title: 'Prototype and pilot', duration: '8–12 weeks', description: 'Build and validate AI solution.' },
    { icon: Gauge, title: 'Productionization', duration: 'Ongoing', description: 'Scale and continuously improve.' }
  ];

  const useCases = [
    { title: 'AI Copilot for Internal Knowledge', description: 'Built an internal AI assistant for searching documentation and policies. Reduced time finding information by 60%.' },
    { title: 'AI for Talent Acquisition', description: 'Developed AI-powered candidate screening. Cut initial screening time by 75%.' },
    { title: 'Natural Language BI', description: 'AI layer on top of dashboards allowing plain English queries. Non-technical users self-serve 80% of data queries.' }
  ];

  return (
    <div data-testid="ai-services-page">
      <Helmet>
        <title>AI-based Services - Cynergy Services</title>
        <meta name="description" content="Practical AI solutions built on your data, tools and workflows. AI copilots, LLM integrations and AI on BI by Cynergy." />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-white to-accent/5" data-testid="ai-hero">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div initial="initial" animate="animate" variants={fadeInUp}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-medium uppercase tracking-widest text-primary">Service</p>
              </div>
              <h1 className="heading-1 text-foreground mb-4">AI-based Services</h1>
              <p className="body-lg mb-6">Practical AI solutions built on your data, tools and workflows.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact">
                  <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="ai-cta-primary">
                    Book an AI discovery workshop
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="ai-cta-secondary">
                    Discuss an AI use case
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_engineering-hub-38/artifacts/nw1rtlb3_AI%20Services.jpeg"
                alt="Artificial intelligence visualization"
                className="w-full rounded-sm shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems & Outcomes Combined */}
      <section className="section-padding bg-white" data-testid="ai-problems-outcomes">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Challenges</p>
              <h2 className="heading-2 text-foreground mb-6">Problems we solve</h2>
              <ul className="space-y-3">
                {problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`ai-problem-${index}`}>
                    <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{problem}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Results</p>
              <h2 className="heading-2 text-foreground mb-6">Outcomes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {outcomes.map((outcome, index) => (
                  <Card key={outcome.title} className="bg-secondary/30 border-0" data-testid={`ai-outcome-${index}`}>
                    <CardContent className="p-4">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center mb-3">
                        <outcome.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="font-semibold text-foreground text-sm mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {outcome.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">{outcome.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capabilities & Engagements */}
      <section className="section-padding bg-secondary/50" data-testid="ai-capabilities">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">What We Build</p>
              <h2 className="heading-2 text-foreground mb-6">Typical AI capabilities</h2>
              <ul className="space-y-3">
                {capabilities.map((item, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`ai-capability-${index}`}>
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Engagement Models</p>
              <h2 className="heading-2 text-foreground mb-6">How we work</h2>
              <div className="space-y-3">
                {engagements.map((engagement, index) => (
                  <Card key={engagement.title} className="border-border/60" data-testid={`ai-engagement-${index}`}>
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <engagement.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {engagement.title}
                          </h3>
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                            {engagement.duration}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{engagement.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-white" data-testid="ai-use-cases">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-primary mb-3">Sample Projects</p>
            <h2 className="heading-2 text-foreground">AI use cases we've delivered</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-secondary/30 border-0" data-testid={`ai-use-case-${index}`}>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {useCase.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{useCase.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary" data-testid="ai-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-2 text-white mb-4">Ready to turn AI ideas into shipped solutions?</h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Let's discuss how we can help you move from AI experimentation to production value.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90" data-testid="ai-final-cta">
                Schedule an AI workshop
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
